import { ApiError, NetworkError } from '../api/errors'

export interface ReportContext {
  /** The registered name of the component the failure happened in. */
  component?: string
  /** React's component stack, for render errors. */
  componentStack?: string
  /** The first segment of the query key (`subscriptions`), never the ids after it. */
  query?: string
  level?: 'error' | 'warning'
}

/**
 * Hand an error to the page's Sentry.
 *
 * The bundle carries no Sentry code of its own: the Webflow head loads Sentry's
 * SDK before this script, and sends any error whose stack runs through the
 * bundle to the homepage-components project (README → Error monitoring). A
 * visitor whose blocker stops Sentry is normal, so this quietly does nothing
 * then — and reporting must never break the widget that failed.
 */
export function report(error: unknown, context: ReportContext = {}): void {
  const sentry = window.Sentry
  if (!sentry) return

  const tags: Record<string, string> = {}
  if (context.component) tags.component = context.component
  if (context.query) tags.query = context.query

  try {
    sentry.captureException(error, {
      level: context.level ?? 'error',
      tags,
      contexts: context.componentStack ? { react: { componentStack: context.componentStack } } : {},
    })
  } catch {
    // Nothing useful to do: the console already has the error.
  }
}

/**
 * How much a failed request matters to us, or null when it is a normal part of
 * using the site and reporting it would only bury the real problems.
 *
 * Mirrors `messageForError`: 400 and 422 are the visitor's input, 401 and 404
 * are credentials or a missing record, 403 is a permission the account lacks.
 * 409 and 415 mean we sent the wrong shape, so they are ours. A request that
 * never arrived is usually the visitor's connection, worth seeing but not
 * alarming; anything that isn't an API error at all is a bug.
 */
export function severityOf(error: unknown): ReportContext['level'] | null {
  if (error instanceof NetworkError) return 'warning'
  if (error instanceof ApiError) {
    if (error.status >= 500 || error.status === 409 || error.status === 415) return 'error'
    return null
  }
  return 'error'
}

/** Report a failed query or mutation, if it is one we should hear about. */
export function reportFailure(error: unknown, context: Omit<ReportContext, 'level'> = {}): void {
  const level = severityOf(error)
  if (level) report(error, { ...context, level })
}
